import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import Header from "../components/Header";
import SideMenu from "../components/SideMenu";
import SiteFooter from "../components/SiteFooter";

const FALLBACKS = {
  about: {
    overline: "Our story",
    title: "Stories shaped by women, for women.",
    body: "Queens Daily is an editorial home for stories that matter — fashion, culture, career, and wellness, shaped by and for women.",
    image: "https://images.pexels.com/photos/8512124/pexels-photo-8512124.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900",
  },
  advertise: {
    overline: "Partnership",
    title: "Reach a readership that cares.",
    body: "Our readers are decision-makers who prize taste, story, and substance.",
    image: "https://images.pexels.com/photos/15712019/pexels-photo-15712019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900",
  },
  contact: {
    overline: "Say hello",
    title: "Let's talk.",
    body: "Tips, feedback, press, or just a kind note — we read everything.",
    image: "https://images.unsplash.com/photo-1557161622-5f50ca344787?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
};

const OVERLINES = { about: "Our story", advertise: "Partnership", contact: "Say hello" };

export default function InfoPage({ kind }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [kind]);

  const fb = FALLBACKS[kind] || FALLBACKS.about;
  const title = settings?.[`${kind}_title`] || fb.title;
  const body = settings?.[`${kind}_body`] || fb.body;
  const image = settings?.[`${kind}_image_url`] || fb.image;

  const cta =
    kind === "advertise" && settings?.advertise_email
      ? { label: settings.advertise_email, href: `mailto:${settings.advertise_email}` }
      : kind === "contact" && settings?.contact_email
      ? { label: settings.contact_email, href: `mailto:${settings.contact_email}` }
      : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <Header onMenuClick={() => setMenuOpen(true)} menuOpen={menuOpen} />
      <SideMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSelectCategory={() => navigate("/")}
        activeCategory={null}
      />

      {/* Hero */}
      <section className="relative w-full h-[60vh] min-h-[420px] overflow-hidden">
        <img src={fileUrl(image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 qd-image-overlay" />
        <div className="absolute inset-0 qd-image-vignette pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 px-6 sm:px-12 lg:px-20 pb-14 text-white">
          <p data-testid="info-overline" className="overline text-white/85 mb-4">
            {OVERLINES[kind]}
          </p>
          <h1
            data-testid="info-title"
            className="font-serif-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl drop-shadow-[0_2px_18px_rgba(0,0,0,0.5)]"
          >
            {title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 sm:px-12 py-16">
        <button
          onClick={() => navigate("/")}
          data-testid="info-back"
          className="inline-flex items-center gap-2 text-sm text-[#76746F] hover:text-[#1A1A1A] transition-colors mb-12"
        >
          <ArrowLeft size={14} /> Back to the deck
        </button>

        <div
          data-testid="info-body"
          className="font-sans-modern text-lg text-[#1A1A1A]/85 leading-[1.85] whitespace-pre-line"
        >
          {body}
        </div>

        {cta && (
          <a
            href={cta.href}
            data-testid="info-cta"
            className="mt-12 inline-flex items-center gap-3 bg-[#1A1A1A] text-white px-6 py-3 rounded-full hover:bg-[#D4A373] transition-colors text-sm"
          >
            <Mail size={14} strokeWidth={1.75} />
            {cta.label}
          </a>
        )}

        {kind === "contact" && settings && (
          <div className="mt-14 pt-8 border-t border-black/10 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-[#76746F]">
            {settings.contact_phone && (
              <div>
                <p className="overline mb-1">Phone</p>
                <p className="text-[#1A1A1A]">{settings.contact_phone}</p>
              </div>
            )}
            {settings.address && (
              <div>
                <p className="overline mb-1">Address</p>
                <p className="text-[#1A1A1A]">{settings.address}</p>
              </div>
            )}
          </div>
        )}
      </article>

      <SiteFooter settings={settings} />
    </div>
  );
}
